/// Lightweight record for a takeoff goal that has been picked by the
/// spawn pipeline but doesn't yet have a materialized `Aircraft` on
/// the runway. The level's goal-driven spawner enqueues these into
/// `GameSession.takeoffQueues` keyed by `<airportName>-<runwayId>`;
/// `promoteNextAircraftInQueue` later promotes them onto the threshold
/// as an `awaitingTakeoff` Aircraft once the runway is clear of
/// arrivals on short final and other ground traffic.
///
/// Mirrors iOS's `PendingAircraft` struct
/// (FlatoutATC/Models/PendingAircraft.swift) in shape and semantics —
/// the queue mechanic is the same, just lives in TypeScript here.

import type { AircraftGoal, AircraftType } from '../types/LevelData'

export interface PendingAircraft {
  /// Stable id that survives the queue→aircraft transition. Used as
  /// the renderer's dot key and (on promotion) becomes the
  /// `Aircraft.id` so callsigns, leaderboard records, and any other
  /// id-keyed bookkeeping correlate cleanly.
  id: string

  /// Pre-allocated callsign so the dot in the radar can label itself
  /// the same as the eventual aircraft (matches iOS's takeoff-queue
  /// label rendering and the data-tag handoff on promotion).
  flightNumber: string

  /// Full goal preserved verbatim. Promotion rebuilds the Aircraft
  /// from it, including SID flight plans (`goal.departure`) and exit-
  /// fix target waypoints (`goal.takeoff.waypointName`).
  goal: AircraftGoal

  type: AircraftType

  /// The airport + runway end this aircraft is queued for. Captured
  /// at enqueue time rather than re-picked at promotion — wind shifts
  /// don't re-route a queued aircraft to a different runway; that's
  /// the simpler, more predictable behaviour iOS uses.
  airportName: string
  runwayId: string

  /// Threshold position in world-px, projected at enqueue time. Used
  /// by the renderer to position the queue dot and by promotion to
  /// place the eventual `awaitingTakeoff` aircraft. iOS re-projects
  /// per call because its renderer is SpriteKit-coordinate-native;
  /// caching here is a small perf win (fewer projector calls per
  /// frame in the dot layer) and a defensive measure against moving-
  /// airport translation drift between enqueue and promotion.
  threshold: { x: number; y: number }

  /// Runway heading (magnetic, degrees) the aircraft will roll on.
  /// Cached so the renderer can stack queue dots along the reverse-
  /// heading vector without re-deriving from the airport's runway
  /// array each frame.
  runwayHeadingDeg: number

  /// Sim-time seconds remaining before this queued slot is scored as
  /// a missed departure window and removed. Decremented in
  /// `GameSession.tickSpawner` alongside the other sim-time timers.
  /// Defaults to 3600 sim-seconds at enqueue (matches iOS's 60-min
  /// budget); a slot that sits behind a long-running short-final
  /// arrival can therefore time out before promotion. iOS commit
  /// 9042d4e fixed the same field there from wall-clock to sim-time;
  /// web's tick loop already runs in sim-time, so this just needs to
  /// be decremented in the tick.
  timeToFirstWaypointSec: number

  /// Whether the eventual `awaitingTakeoff` aircraft is AI-controlled
  /// or player-flown. Currently always `false` (web port hasn't
  /// ported the AI controller) but threaded through anyway so the
  /// queue is forward-compatible.
  isAIControlled: boolean
}

/// Default sim-seconds-to-first-waypoint budget for a freshly enqueued
/// takeoff slot. Matches iOS's `deadline = TimeInterval(3600)` at
/// AircraftSpawner.swift:464.
export const DEFAULT_TAKEOFF_QUEUE_DEADLINE_SEC = 3600

/// Compose the queue key from airport name + runway id. Centralized
/// because the format is referenced from enqueue, promotion, renderer,
/// and tests — any future change (e.g. adding a heading suffix for
/// runways with split SIDs) should land here.
export function queueKeyFor(airportName: string, runwayId: string): string {
  return `${airportName}-${runwayId}`
}
